/**
 * Utility functions for checklist operations
 */

/**
 * Generate unique ID for checklist items
 * @returns {string} - Unique ID
 */
export const generateChecklistItemId = () => {
  return `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create new checklist item object
 * @param {string} type - Item type: "checkbox", "image_required", "comment_required"
 * @param {string} label - Item label/description
 * @returns {Object} - New checklist item
 */
export const createChecklistItem = (type, label) => {
  const baseItem = {
    id: generateChecklistItemId(),
    type,
    label,
    completed: false,
    createdAt: new Date().toISOString(),
  };

  switch (type) {
    case "checkbox":
      return baseItem;
    case "image_required":
      return {
        ...baseItem,
        imageUrl: null,
        imagePath: null,
      };
    case "comment_required":
      return {
        ...baseItem,
        comment: "",
      };
    default:
      return baseItem;
  }
};

/**
 * Create new checklist object
 * @param {string} name - Checklist name
 * @returns {Object} - New checklist
 */
export const createChecklist = (name) => {
  return {
    id: generateChecklistItemId(),
    name,
    items: [],
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString(),
  };
};

/**
 * Calculate checklist completion percentage
 * @param {Array} items - Checklist items
 * @returns {number} - Completion percentage (0-100)
 */
export const calculateChecklistCompletion = (items) => {
  if (!items || items.length === 0) return 0;
  const completedCount = items.filter((item) => item.completed).length;
  return Math.round((completedCount / items.length) * 100);
};

/**
 * Get total items count across all checklists
 * @param {Array} checklists - Array of checklists
 * @returns {number} - Total items count
 */
export const getTotalItemsCount = (checklists) => {
  if (!checklists || checklists.length === 0) return 0;
  return checklists.reduce((sum, checklist) => sum + (checklist.items?.length || 0), 0);
};

/**
 * Get completed items count across all checklists
 * @param {Array} checklists - Array of checklists
 * @returns {number} - Completed items count
 */
export const getCompletedItemsCount = (checklists) => {
  if (!checklists || checklists.length === 0) return 0;
  return checklists.reduce(
    (sum, checklist) =>
      sum + (checklist.items?.filter((item) => item.completed).length || 0),
    0
  );
};

/**
 * Validate checklist item completion
 * @param {Object} item - Checklist item
 * @returns {boolean} - True if item can be marked as completed
 */
export const canCompleteItem = (item) => {
  switch (item.type) {
    case "checkbox":
      return true; // Checkbox can always be toggled
    case "image_required":
      return item.imageUrl && item.imageUrl.trim() !== "";
    case "comment_required":
      return item.comment && item.comment.trim() !== "";
    default:
      return false;
  }
};

/**
 * Update checklist item completion status
 * @param {Object} item - Checklist item
 * @param {boolean} completed - New completion status
 * @returns {Object} - Updated item
 */
export const updateItemCompletion = (item, completed) => {
  // Only allow completion if requirements are met
  if (completed && !canCompleteItem(item)) {
    return item;
  }
  return { ...item, completed };
};

/**
 * Reorder items in array using drag-and-drop indices
 * @param {Array} items - Original items array
 * @param {number} fromIndex - Source index
 * @param {number} toIndex - Destination index
 * @returns {Array} - Reordered items array
 */
export const reorderItems = (items, fromIndex, toIndex) => {
  const result = Array.from(items);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
};

/**
 * Get checklist summary for display
 * @param {Array} checklists - Array of checklists
 * @returns {Object} - Summary object with counts and percentages
 */
export const getChecklistSummary = (checklists) => {
  const totalItems = getTotalItemsCount(checklists);
  const completedItems = getCompletedItemsCount(checklists);
  const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return {
    totalChecklists: checklists?.length || 0,
    totalItems,
    completedItems,
    percentage,
    isComplete: totalItems > 0 && completedItems === totalItems,
  };
};
